"use client";

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Search, X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/i18n/routing";
import { EJHEALTH_SEARCH_ROUTE } from "@/lib/routes";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { ejHealthSuggest } from "@/fixtures/ej-health/search/taxonomy";

/**
 * The search field, with type-ahead.
 *
 * QA: "should have auto suggestion when inputting text". Suggestions are
 * article titles first, then category names, then the tags those categories
 * declare — so a partial word offers something to open rather than only a query
 * to run. Arrow keys move through the list, Enter takes the highlighted one,
 * Escape closes it.
 *
 * `appearance-none` plus the `::-webkit-search-cancel-button` reset in
 * globals.css removes the engine's own clear widget; the ✕ here is a real
 * button in the product's palette, and it exists only while there is something
 * to clear. Same component shape as `ej-tech/search/search-bar.tsx`.
 */
export function EjHealthSearchBar({
  initialQuery = "",
  autoFocus = false,
  onNavigate,
  placeholder = "搜尋信健康文章…",
}: {
  initialQuery?: string;
  autoFocus?: boolean;
  /** Lets the masthead close its panel when a search runs. */
  onNavigate?: () => void;
  placeholder?: string;
}) {
  const [value, setValue] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  // The masthead panel and the mobile drawer both mount one of these, so the
  // listbox id has to be per-instance or `aria-controls` points at two nodes.
  const listId = `ejhealth-search-suggestions-${useId()}`;

  /**
   * Keep the field showing the query the page is answering.
   *
   * `useState(initialQuery)` only seeds on MOUNT, and every route in this
   * vertical is a client navigation — so submitting a search re-rendered the
   * same bar with the old text (or none), and the results below did not match
   * what the box said. QA: "after search a keyword, the keyword should show in
   * the search bar". Syncing on the prop also covers the back button and a
   * suggestion picked from the masthead.
   */
  useEffect(() => {
    setValue(initialQuery);
  }, [initialQuery]);

  const suggestions = useMemo(() => ejHealthSuggest(value), [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const go = (q: string) => {
    const trimmed = q.trim();
    setOpen(false);
    onNavigate?.();
    router.push(
      trimmed
        ? `${EJHEALTH_SEARCH_ROUTE}?q=${encodeURIComponent(trimmed)}`
        : EJHEALTH_SEARCH_ROUTE,
    );
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    go(active >= 0 && suggestions[active] ? suggestions[active] : value);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <form
        onSubmit={onSubmit}
        role="search"
        className="flex w-full items-stretch"
      >
        <div
          className="relative flex min-w-0 flex-1 items-center"
          style={{
            border: `1px solid ${EJHEALTH_COLORS.dividerStrong}`,
            borderRight: "none",
            backgroundColor: EJHEALTH_COLORS.surface,
          }}
        >
          <Search
            className="ml-4 h-4 w-4 shrink-0"
            style={{ color: EJHEALTH_COLORS.textMuted }}
          />
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setActive(-1);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={open && suggestions.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-label="搜尋"
            autoComplete="off"
            autoFocus={autoFocus}
            suppressHydrationWarning
            placeholder={placeholder}
            className="ejhealth-search-input min-w-0 flex-1 appearance-none bg-transparent py-3 pl-3 pr-2 outline-none"
            style={{
              ...EJHEALTH_TYPE.body,
              fontSize: 16,
              color: EJHEALTH_COLORS.dark,
            }}
          />
          {value.length > 0 && (
            <button
              type="button"
              aria-label="清除搜尋"
              onClick={() => {
                setValue("");
                setOpen(false);
                inputRef.current?.focus();
                // Emptying the field is only half of it: the RESULTS are
                // rendered from `?q=` on the server, so a cleared box would sit
                // above the old result set.
                if (searchParams.toString()) router.replace(EJHEALTH_SEARCH_ROUTE);
              }}
              className="mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors duration-150 hover:bg-black/[0.06] motion-reduce:transition-none"
            >
              <X className="h-4 w-4" style={{ color: EJHEALTH_COLORS.textMuted }} />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="inline-flex shrink-0 items-center justify-center bg-[color:var(--rest-bg)] px-6 text-[color:var(--rest-fg)] transition-colors duration-150 hover:bg-[color:var(--hover-bg)] motion-reduce:transition-none"
          style={
            {
              ...EJHEALTH_TYPE.button,
              ["--rest-bg" as string]: EJHEALTH_COLORS.primary,
              ["--rest-fg" as string]: EJHEALTH_COLORS.textOnDark,
              ["--hover-bg" as string]: EJHEALTH_COLORS.primaryDark,
            } as React.CSSProperties
          }
        >
          搜尋
        </button>
      </form>

      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          data-ejhealth-suggestions
          className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden bg-white py-1 shadow-lg"
          style={{ border: `1px solid ${EJHEALTH_COLORS.divider}` }}
        >
          {suggestions.map((s, i) => (
            <li key={s} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  setValue(s);
                  go(s);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-black/[0.04] motion-reduce:transition-none"
                style={{
                  ...EJHEALTH_TYPE.body,
                  fontSize: 14,
                  color: EJHEALTH_COLORS.dark,
                  backgroundColor:
                    i === active ? "rgba(0,0,0,0.04)" : "transparent",
                }}
              >
                <Search
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: EJHEALTH_COLORS.textMuted }}
                />
                <span className="truncate">{s}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
